// web/src/lib/api.ts — ONE interface, TWO impls (CONTRACT §"The Api interface"). Screens import `api`, never
// fetch directly. Switched by VITE_USE_MOCK: mock data renders the whole dashboard before any backend exists
// ("see-it-first"). Default = mock unless VITE_USE_MOCK === '0' (CONTRACT + D14 §2).
import type {
  Me, HomeFeed, Site, SitePreferences, SiteDetail, Build, BuildSummary, RunDetail, RunSummary, SupportThread, BillingState,
  PaymentsSummaryDto, Settings, AdminOverview, AdminCustomerRow, AdminCustomerDetail, AdminUserRow,
  AdminBuildRow, AdminBuildDetail, ConnectStatus, OnboardingDto, RunStatus,
  DnsListDto, DnsPlanDto, DnsChangeSetSummary,
  AdminTaskRow, AdminTaskSummary, AdminTaskDetail, CreditState, TopUpPack,
  AdminPlatformMetrics, AdminCharts, AdminOrgRow, AdminOrgDetail, AdminHealth, AdminSignals,
  MediaAsset, MediaPresign, PlanInterval,
  MyPreview, PreviewFormBody, AdminLead, PreviewLeadStatus, AttachDemoBody, SiteReport, Proposal, ProposalStatus,
  BoardStage, AdminCounts, AdminReadyRow, AdminSiteRow, AdminSiteDetail, RegistrySite, LifecycleTask, LifecycleStatus,
  OutboxItem, OutboxStatus, UpdateRun, UpdateSpend, UpdateDue, AdminReport, AdminProposal, AdminProposalReview, AdminInvite,
  MonitorStateDto, FleetJson,
} from './types';

/** 50 §8: 30 days by default, 7/30/90 toggles. The API clamps anything else. */
export type AdminWindow = 7 | 30 | 90;
import { mockApi } from './mockApi';
import { httpApi } from './httpApi';

export interface Api {
  me(): Promise<Me>;
  home(): Promise<HomeFeed>;
  listSites(): Promise<Site[]>;
  getSite(id: string): Promise<SiteDetail>;
  /** The client's own preferences (portal spec 11): report emails, update mode, GBP managed. */
  setPreferences(siteId: string, p: Partial<SitePreferences>): Promise<Site>;
  /** Accept a client invite link (portal spec 13): links this login to the invited site's workspace. */
  acceptInvite(token: string): Promise<{ orgId: string; siteId: string }>;
  // Free preview (portal spec 24): the prospect's screen + the form (POST 404s while PREVIEW_FORM_ENABLED is off)
  myPreview(): Promise<MyPreview>;
  sendPreview(body: PreviewFormBody): Promise<{ id: string; status: PreviewLeadStatus; created: boolean }>;
  // website builds + site CRUD
  createBuild(input: { mode: 'engaged' | 'spec'; businessName: string; links: string[]; notes?: string }): Promise<{ id: string; status: string }>;
  listBuilds(): Promise<BuildSummary[]>;
  buildInterview(id: string, message: string): Promise<{ reply: string; done: boolean; agreedPlanMd?: string }>;
  approveBuild(id: string): Promise<{ url: string }>;
  retryBuild(id: string): Promise<{ ok: true; phase: string; from?: string }>; // doc 13: resubmit from checkpoint
  runBuildQa(id: string): Promise<{ ok: true; status: string }>; // owner "Run final checks" at awaiting_qa
  getBuild(id: string): Promise<Build>;
  claimSite(id: string): Promise<{ ok: true; state: string }>;
  deleteSite(id: string): Promise<{ ok: true }>;
  siteQa(id: string): Promise<{ ok: true }>; // customer "Run final checks" on a live site (CHALICE C4)
  listRequests(): Promise<RunSummary[]>;
  // `assetIds` are photos/video already uploaded and processed against the SITE (doc 30 §8 — the
  // uploader runs before the request exists, so the request adopts them at submit).
  submitRequest(siteId: string, title: string, body: string, kind?: string, assetIds?: string[]): Promise<{ id: string; url?: string; status?: RunStatus }>;
  getRun(id: string): Promise<RunDetail>;
  // Chalice tasks (CHALICE_TASKS_SPEC v2): the chat drives clarification + tweaks; approve = "Make
  // it live" (merge dev→main); rollback = revert the go-live merge. No caps in v1.
  sendMessage(runId: string, body: string): Promise<{ ok: true }>;
  // portal spec 19: status is merging, or publish_waiting while another change goes live first
  approveRequest(runId: string): Promise<{ ok: true; status?: RunStatus }>;
  rollbackRequest(runId: string): Promise<{ ok: true }>;
  uploadSas(filename: string, contentType: string): Promise<{ uploadUrl: string; blobUrl: string }>;
  // ── Media pipeline (30_MEDIA_PIPELINE.md Phase A). The BROWSER does the upload: presignMedia
  //    returns URLs that point at S3, MediaUpload.tsx PUTs to them directly, and no file byte ever
  //    reaches the API (§7 item 7). Everything below is metadata.
  presignMedia(siteId: string, files: { filename: string; contentType: string; bytes: number }[]): Promise<MediaPresign>;
  completeMedia(assetId: string, body?: { parts?: { partNumber: number; etag: string }[]; caption?: string }): Promise<MediaAsset>;
  getMediaAsset(assetId: string): Promise<MediaAsset>;
  deleteMedia(assetId: string): Promise<{ ok: true }>;
  listStagedMedia(siteId: string): Promise<MediaAsset[]>;
  listRequestMedia(requestId: string): Promise<MediaAsset[]>;
  paymentsSummary(): Promise<PaymentsSummaryDto>; // portal spec 16: their money, never our fee
  connectSession(): Promise<{ clientSecret: string }>; // embedded Connect onboarding (doc 05)
  connectStatus(): Promise<ConnectStatus>;
  portal(): Promise<{ url: string }>;
  checkout(siteId: string, interval: PlanInterval): Promise<{ url: string }>; // Website care for one site (spec 15)
  getBilling(): Promise<BillingState>;
  // Update credits (doc 12). getCredits is the balance behind every meter; the two below are
  // Phase B's purchase rail — packs come from the API so the price and the credit count cannot
  // drift from 02 §2, and startTopUpCheckout returns a one-off Stripe Checkout url (never a
  // subscription change, doc 12 §2). Nothing is granted until the webhook confirms payment.
  getCredits(): Promise<CreditState>;
  listTopUpPacks(): Promise<TopUpPack[]>;
  startTopUpCheckout(packId: string): Promise<{ url: string }>;
  listThreads(): Promise<SupportThread[]>;
  getThread(id: string): Promise<SupportThread>;
  postQuestion(subject: string, body: string): Promise<{ threadId: string }>;
  replyThread(id: string, body: string): Promise<{ ok: true }>;
  getSettings(): Promise<Settings>;
  saveSettings(p: Partial<Settings>): Promise<{ ok: true }>;
  /** One site's published monthly reports, newest first (portal spec 17). */
  listReports(siteId: string): Promise<SiteReport[]>;
  /** This month's suggestions for one site, or null (none yet, or KC is still reviewing them) — portal spec 18. */
  currentProposal(siteId: string): Promise<Proposal | null>;
  /** Yes / No thanks on one idea (Yellow only). 409 once it is answered. */
  decideItem(proposalId: string, itemId: string, decision: 'approve' | 'decline', note?: string): Promise<{ ok: true; proposalStatus: ProposalStatus }>;
  // The go-live checklist (portal spec 14). refresh = "Check again" (bypasses the caches, rate-limited per site).
  onboarding(siteId: string, refresh?: boolean): Promise<OnboardingDto>;
  // Sale alerts (Settings; the go-live brain can also set them)
  setAlerts(siteId: string, channel: 'email' | 'sms' | 'both', email?: string, phone?: string): Promise<{ ok: true }>;
  testAlert(siteId: string): Promise<{ sent: true }>;
  // AI DNS tab (per-site, scoped server-side)
  getDns(siteId: string): Promise<DnsListDto>;
  planDns(siteId: string, body: { intent?: string; pastedDns?: string }): Promise<DnsPlanDto>;
  applyDns(siteId: string, csId: string, confirmed: boolean): Promise<{ ok: true }>;
  rejectDns(siteId: string, csId: string): Promise<{ ok: true }>;
  revertDns(siteId: string, csId: string): Promise<{ ok: true }>;
  dnsHistory(siteId: string): Promise<DnsChangeSetSummary[]>;
  // admin
  adminOverview(): Promise<AdminOverview>;
  adminCustomers(): Promise<AdminCustomerRow[]>;
  adminCustomer(id: string): Promise<AdminCustomerDetail>;
  adminUsers(): Promise<AdminUserRow[]>;
  // admin — go-live (portal spec 14, D2): the checklist with nameservers for KC's call, and the Start go-live button
  adminOnboarding(slug: string, refresh?: boolean): Promise<OnboardingDto>;
  adminStartGoLive(slug: string, force?: boolean): Promise<{ taskId: string; stage: 'going_live' }>;
  // admin — free-preview leads (portal spec 24): KC's Leads screen
  adminLeads(status?: PreviewLeadStatus): Promise<AdminLead[]>;
  adminLeadStatus(id: string, status: 'new' | 'building' | 'declined'): Promise<{ id: string; status: PreviewLeadStatus }>;
  adminAttachDemo(id: string, body: AttachDemoBody): Promise<{ ok: true; siteId: string; outboxId: string | null }>;
  // admin — QA gate (doc 04)
  adminBuilds(): Promise<AdminBuildRow[]>;
  adminBuild(id: string): Promise<AdminBuildDetail>;
  adminApproveBuild(id: string): Promise<{ ok: true; status: string }>;
  adminRejectBuild(id: string, reason: string, requeue?: boolean): Promise<{ ok: true; status: string; requeuedId?: string }>;
  adminRunQa(id: string): Promise<{ ok: true; status: string }>; // on-demand "Run final checks"
  // admin — chalice tasks (CHALICE §4g): visibility + Retry/Rollback/View chat
  adminTasks(): Promise<AdminTaskRow[]>;
  adminTasksSummary(): Promise<AdminTaskSummary>;
  adminTask(id: string): Promise<AdminTaskDetail>;
  adminRetryTask(id: string): Promise<{ ok: true; phase: string }>;
  adminRollbackTask(id: string): Promise<{ ok: true }>;
  // admin — master console (chalice-v2 50_ADMIN_CONSOLE.md). Money/usage/quality, org-keyed.
  // NOTE the id space: adminCustomer(id) above takes a STRIPE customer id (the D15 Connect
  // view); everything below takes an ORG id. They are different keys over different sources.
  adminMetrics(days: AdminWindow): Promise<AdminPlatformMetrics>;
  adminCharts(days: AdminWindow): Promise<AdminCharts>;
  adminOrgs(days: AdminWindow): Promise<AdminOrgRow[]>;
  adminOrg(orgId: string, days: AdminWindow): Promise<AdminOrgDetail>;
  adminHealth(days: AdminWindow): Promise<AdminHealth>;
  // Learnings loop (doc 51 §5): the signals cluster view + last distillation PR link. Read-only —
  // the distiller runs on its own monthly cron and there is deliberately no trigger endpoint.
  adminSignals(days: AdminWindow): Promise<AdminSignals>;
  // The three — and only three — console mutations (50 §2). Each writes an audit row.
  adminGrantCredits(orgId: string, credits: number, note?: string): Promise<{ ok: true; creditsRemaining: number }>;
  adminSetPaused(orgId: string, paused: boolean, reason?: string): Promise<{ ok: true; killed: boolean }>;
  adminRetryRequest(id: string): Promise<{ ok: true; phase: string }>;

  // ── admin console (portal spec 22): the board, client detail and the queues. Each route is its owner spec's ──
  adminBoard(): Promise<BoardStage[]>;                                  // 22
  adminCounts(): Promise<AdminCounts>;                                  // 22 (nav badges, polled every 60 s)
  adminReady(): Promise<AdminReadyRow[]>;                               // 22: sold/finishing with account + plan done
  adminSites(): Promise<AdminSiteRow[]>;                                // 22: thin list (names, slug picker)
  adminSite(slug: string): Promise<AdminSiteDetail>;                    // 22
  adminPatchSite(slug: string, patch: Record<string, unknown>, force?: boolean): Promise<RegistrySite>; // 22 → registry
  adminMoveOver(slug: string, patch: Record<string, unknown>): Promise<RegistrySite>; // 22 (X12)
  adminReleaseRequestLock(slug: string): Promise<{ released: boolean; holder: string | null }>; // 22
  adminFleet(): Promise<FleetJson>;                                     // 22
  adminReport(id: string): Promise<AdminReport>;                        // 22
  adminPublishReport(id: string): Promise<{ ok: true; emailed: boolean; outboxId?: string }>; // 22 → spec 17
  adminUnpublishReport(id: string): Promise<{ ok: true }>;              // 22 → spec 17
  adminRemoveLogin(orgId: string, userId: string): Promise<{ ok: true; removed: string; loginsLeft: number }>; // 22
  adminInvites(slug: string): Promise<AdminInvite[]>;                   // 13
  adminInvite(slug: string, email: string, businessName?: string): Promise<{ id: string; url: string; expiresAt: string }>; // 13
  adminRevokeInvite(id: string): Promise<{ ok: true; revoked: boolean }>; // 13
  adminLifecycle(status?: LifecycleStatus): Promise<LifecycleTask[]>;   // 25
  adminCancelTask(id: string): Promise<LifecycleTask>;                  // 25
  adminRetryLifecycleTask(id: string): Promise<LifecycleTask>;          // 25 (failed → open)
  adminAttachTask(id: string, slug: string): Promise<unknown>;          // 25
  adminSiteOff(slug: string): Promise<unknown>;                         // 25 (409 while SITE_OFF_ENABLED is off)
  adminSiteOn(slug: string): Promise<unknown>;                          // 25
  adminOffboard(slug: string, cancelPlan?: boolean): Promise<LifecycleTask>; // 25
  adminOutbox(status?: OutboxStatus): Promise<OutboxItem[]>;            // 25
  adminEditOutbox(id: string, p: { subject?: string; body?: string }): Promise<OutboxItem>; // 25
  adminApproveOutbox(id: string): Promise<OutboxItem>;                  // 25
  adminCancelOutbox(id: string): Promise<OutboxItem>;                   // 25
  adminRetryOutbox(id: string): Promise<OutboxItem>;                    // 25
  adminMonitorToggle(slug: string, enabled: boolean, host?: string): Promise<MonitorStateDto>; // 26
  adminMonitorCheck(slug: string): Promise<MonitorStateDto>;            // 26
  adminRunUpdate(slug: string, postReport?: boolean): Promise<UpdateRun>; // 20
  adminUpdateRuns(limit?: number): Promise<UpdateRun[]>;                // 20
  adminUpdateDue(): Promise<UpdateDue>;                                 // 20 (U2 manual phase)
  adminUpdateSpend(month?: string): Promise<UpdateSpend>;               // 20
  adminReleaseRun(id: string): Promise<{ ok: true; status: string; proposalStatus: string | null; reportEmailed: boolean | null }>; // 20
  adminRetryRun(id: string): Promise<{ ok: true; status: string }>;     // 20 (held monthly runs)
  adminCancelRun(id: string): Promise<unknown>;                         // 20
  adminProposalsReview(): Promise<AdminProposalReview[]>;               // 18
  adminProposal(id: string): Promise<AdminProposal>;                    // 18
  adminDecideOnBehalf(proposalId: string, itemId: string, decision: 'approve' | 'decline', note?: string): Promise<{ ok: true; proposalStatus: string }>; // 18
  adminCreateRequest(slug: string, b: { title: string; body: string; source: 'email' | 'admin' }): Promise<{ id: string; status: string }>; // 19
  adminUploadSas(slug: string, filename: string, contentType: string): Promise<{ uploadUrl: string; blobUrl: string }>; // 19
  adminReleasePublishLock(slug: string): Promise<{ released: boolean; holder: string | null }>; // 19
}

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== '0';

export const api: Api = USE_MOCK ? mockApi : httpApi;
export const IS_MOCK = USE_MOCK;
